import { ThemeUIStyleObject, ResponsiveStyleValue } from "theme-ui";
import React, { Fragment } from "react";

import { Card, CardLink, getImageWidthForCard } from "../Card";
import { Box } from "@shared-components/Box/Box";
import { Heading } from "@shared-components/Heading/Heading";
import { Text } from "@shared-components/Text/Text";
import { AspectRatio } from "@shared-components/AspectRatio/AspectRatio";

import {
  CardStyles,
  CardRedbgStyles,
  CardGreenbgStyles,
  CardInnerStyles,
  CardSplashStyles,
  CardSplashBottomStyles,
  ThreeLeafStyles,
  ThreeLeafRedStyles,
  CardAspectRatioGradientStyles,
  CardParagraphTextStyles,
  CardAspectRatioStyles,
} from "../styles";

import { EventContentElement } from "src/carlsberg-headless/types/content-elements/Event";
import { PrimitiveProps } from "@styles/types";
import { TAXONOMIES_MAP } from "../../../../carlsberg-headless/taxonomies-map";
import { useSiteContext } from "../../../../context/site/useSiteContext";
import ParseHtmlToReact, {
  richTextStyles,
} from "@shared-components/ParseHtmlToReact/ParseHtmlToReact";
import { UmbracoBackgroundImage } from "@shared-components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage";

import ThreeLeafCloverSvg from "@svg-icons/three-leaf-clover.component.svg";
import { Icon } from "@shared-components/Icon/Icon";
import { THEMES_MAP } from "../../../../carlsberg-headless/themes-map";
import { useDateFormatter } from "../../../utils/load-and-format-date";
export interface SplashProps extends PrimitiveProps {
  text: string;
  variant?: string;
}
const CardSplash: React.FC<SplashProps> = ({ text, variant }) => (
  <Box
    sx={{
      ...CardSplashStyles,
      ...(variant === "cancelled" && CardSplashBottomStyles),
    }}
    variant={`cardSplash.${variant}`}
  >
    {text && <ParseHtmlToReact html={text} />}
  </Box>
);

const BoxContainer: React.FC<EventCardProps> = ({ ...props }) => {
  const { currentCulture } = useSiteContext();

  const dateFormatToken = "dd LLL yyyy";

  const formattedStartDate = useDateFormatter(
    props.startDate as string,
    dateFormatToken,
    currentCulture
  );

  const isToday = (date?: string) => {
    if (!date) {
      return false;
    }
    const today = new Date();
    const _date = new Date(date);
    return (
      _date.getDate() === today.getDate() &&
      _date.getMonth() === today.getMonth() &&
      _date.getFullYear() === today.getFullYear()
    );
  };

  const formattedEndDate = props.endDate
    ? useDateFormatter(props.endDate as string, dateFormatToken, currentCulture)
    : null;

  let splashType = "";
  switch (true) {
    case props.taxonomies.includes(
      TAXONOMIES_MAP.event_module.eventcard_custom
    ):
      splashType = "custom";
      break;
    case props.taxonomies.includes(
      TAXONOMIES_MAP.event_module.eventcard_cancelled
    ):
      splashType = "cancelled";
      break;
  }

  return (
    <Fragment>
      {props.calloutTextTop && splashType && (
        <CardSplash text={props.calloutTextTop} variant={splashType} />
      )}
      <Box
        sx={
          props.image1 ? CardAspectRatioGradientStyles : CardAspectRatioStyles
        }
      >
        <Box
          sx={{
            ...CardInnerStyles,
          }}
        >
          {props.startDate && props.startDate !== "0001-01-01T00:00:00" && (
            <Text as="p" variant="cardSectionText">
              {props.startDate && props.todayText && isToday(props.startDate)
                ? props.todayText
                : formattedStartDate}
              {formattedEndDate && (
                <Fragment>
                  {" - "}
                  {formattedEndDate}
                </Fragment>
              )}
            </Text>
          )}

          {props.title && (
            <Heading
              as="h2"
              variant={"h3"}
              sx={{ mt: 2, letterSpacing: "1px" }}
            >
              {props.title}
            </Heading>
          )}

          {props.calloutTextBottom && (
            <Box sx={{ mb: 0, display: ["none", "block"] }}>
              <ParseHtmlToReact
                sx={{ ...richTextStyles, ...CardParagraphTextStyles }}
                html={props.calloutTextBottom}
              />
            </Box>
          )}

          {props.price && (
            <Fragment>
              {props.priceText && (
                <Heading as="h3" variant="cardSectionText" sx={{ mt: 6 }}>
                  {props.priceText}
                </Heading>
              )}

              <Text
                as="p"
                variant="cardSectionText"
                sx={{ fontSize: "paragraphMedium", fontFamily: "heading" }}
              >
                {props.price}
              </Text>
            </Fragment>
          )}
        </Box>
      </Box>
    </Fragment>
  );
};

const AspectRatioBox: React.FC<EventCardProps> = ({ ...props }) => {
  const hasRedBackground = props.themes.includes(
    THEMES_MAP.cards_module.card.cards_module_red_background
  );

  const imageWidth =
    props.ratio &&
    (getImageWidthForCard(props.ratio) as ResponsiveStyleValue<
      string | number
    >);

  const mobileRatioOverride = Array.isArray(props.ratio)
    ? ["1:1", ...props.ratio.slice(1)]
    : props.ratio;

  return (
    <AspectRatio ratio={mobileRatioOverride} sx={{ height: "100%" }}>
      <Icon
        sx={{
          ...ThreeLeafStyles,
          ...(hasRedBackground && ThreeLeafRedStyles),
        }}
        svg={ThreeLeafCloverSvg}
      />
      {props.image1 ? (
        <UmbracoBackgroundImage
          image={props.image1}
          {...(imageWidth ? { width: imageWidth } : { width: [340, 700] })}
        >
          <BoxContainer {...props} />
        </UmbracoBackgroundImage>
      ) : (
        <BoxContainer {...props} />
      )}
    </AspectRatio>
  );
};

export interface EventCardProps extends EventContentElement {
  ratio: string | string[];
  sx: ThemeUIStyleObject;
  todayText?: string;
  priceText?: string;
}

const EventCard: React.FC<EventCardProps> = ({ ...props }) => {
  const hasRedBackground = props.themes.includes(
    THEMES_MAP.cards_module.card.cards_module_red_background
  );
  const hasGreenBackground = props.themes.includes(
    THEMES_MAP.cards_module.card.cards_module_green_background
  );

  return (
    <Card
      key={props.cbKey}
      sx={{
        ...CardStyles,
        ...(hasRedBackground && CardRedbgStyles),
        ...(hasGreenBackground && CardGreenbgStyles),
        ...props.sx,
      }}
    >
      {props.link ? (
        <CardLink {...props.link}>
          <AspectRatioBox {...props} />
        </CardLink>
      ) : (
        <AspectRatioBox {...props} />
      )}
    </Card>
  );
};

EventCard.defaultProps = {};

export { EventCard };
